// Bounds a promise that has no timeout of its own. Rejects with `message` if `ms` elapses first.
// The loser of the race is NOT cancelled — nothing here can cancel an Office.js batch or a fetch —
// so a caller that rejects on the timeout must decide separately what to do with the abandoned
// promise (typically attach a no-op catch so its late settlement cannot surface as an unhandled
// rejection). The timer is always cleared, so a fast win leaves nothing pending.
export function withTimeout(promise, ms, message) {
  let timer = null
  const timeout = new Promise((resolve, reject) => {
    timer = setTimeout(() => reject(new Error(message)), ms)
  })
  return Promise.race([promise, timeout]).finally(() => clearTimeout(timer))
}

export function createDeferred() {
  let _resolve
  let _reject
  let _promise
  let resolved = false
  let result

  async function promise() {
    if (resolved) {
      return result
    }

    // ensuring at most 1 promise runs at anytime.
    if (_promise) {
      return _promise
    }

    _promise = new Promise((res, rej) => {
      _resolve = res
      _reject = rej
    })
  }

  function resolve(val) {
    _resolve(val)
    result = val
    resolved = true
    _promise = null
  }

  function reject(error) {
    _reject(error)
    result = undefined
    resolved = false
    _promise = null
  }

  return { promise, resolve, reject }
}

/**
 * Coalescing requests to avoid redundant fetching/payload handling.
 *
 * Case 1: Success - requests during flight share promise, after resolution get cached result
 *   R1─[═════════════════════════════════]✓─────────────────
 *       R2─────────>                          ├──R4 (cached)
 *          R3────────────>                    └──R5 (cached)
 *        in-flight (await same promise)     instant
 *
 * Case 2: Failure then retry - error clears state, next request starts fresh
 *   R1─[════════════════════════════]✗───R4─[════════════════════]✓───
 *       R2─────────>                          R5 ─────────>
 *          R3────────────>
 *     in-flight (all will fail)            triggers the promise again
 */
export function coalesce(asyncFunc) {
  let resolved = false
  let result
  let promise

  async function run() {
    if (resolved) {
      return result
    }

    if (promise) {
      return promise
    }

    try {
      promise = asyncFunc()
      result = await promise
      resolved = true
      return result
    } finally {
      promise = null
    }
  }

  return {
    run,
  }
}
